// src/admin/AdminClients.jsx
import React, { useEffect, useState } from 'react';
import axios from 'axios';

const API = 'https://madadline.com/api';

const authHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
});

const toWhatsAppNumber = (phone) => (phone || '').replace(/[^\d]/g, '');

function AdminClients() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all'); // all | account | guest

  // Édition inline
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [saving, setSaving] = useState(false);

  // Suppression
  const [deletingId, setDeletingId] = useState(null);

  // Détail (modal)
  const [detailClient, setDetailClient] = useState(null);

  /* =========================================================
     CHARGEMENT
  ========================================================= */
  const fetchClients = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(`${API}/users`, authHeaders());
      setClients(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setError('Impossible de charger les clients.');
      setClients([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchClients(); }, []);

  /* ⭐ Cliente créée automatiquement à la commande (pas de vrai email / connexion) */
  const isGuest = (c) => Boolean(c.is_guest);

  /* =========================================================
     ÉDITION
  ========================================================= */
  const startEdit = (c) => {
    setEditingId(c.id);
    setEditName(c.name || '');
    setEditEmail(isGuest(c) ? '' : (c.email || ''));
    setEditPhone(c.phone || '');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName('');
    setEditEmail('');
    setEditPhone('');
  };

  const saveEdit = async (c) => {
    const guest = isGuest(c);

    if (!editName.trim() || (!guest && !editEmail.trim())) {
      alert(guest ? 'Le nom est obligatoire.' : "Le nom et l'email sont obligatoires.");
      return;
    }

    const payload = { name: editName.trim(), phone: editPhone.trim() };
    if (!guest) payload.email = editEmail.trim();

    setSaving(true);
    try {
      await axios.put(`${API}/users/${c.id}`, payload, authHeaders());
      setClients(prev =>
        prev.map(x => x.id === c.id ? { ...x, ...payload } : x)
      );
      cancelEdit();
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.error || e.response?.data?.message || 'Erreur lors de la modification.');
    } finally {
      setSaving(false);
    }
  };

  /* =========================================================
     SUPPRESSION
  ========================================================= */
  const deleteClient = async (id) => {
    if (!window.confirm("Supprimer définitivement ce client ? Ses commandes resteront dans l'historique.")) return;
    setDeletingId(id);
    try {
      await axios.delete(`${API}/users/${id}`, authHeaders());
      setClients(prev => prev.filter(c => c.id !== id));
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.error || 'Erreur lors de la suppression.');
    } finally {
      setDeletingId(null);
    }
  };

  /* =========================================================
     FILTRES
  ========================================================= */
  const filtered = clients.filter(c => {
    if (typeFilter === 'guest' && !isGuest(c)) return false;
    if (typeFilter === 'account' && isGuest(c)) return false;

    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (c.name || '').toLowerCase().includes(q) ||
      (!isGuest(c) && (c.email || '').toLowerCase().includes(q)) ||
      (c.phone || '').replace(/\s/g, '').includes(q.replace(/\s/g, '')) ||
      (c.city || '').toLowerCase().includes(q)
    );
  });

  const guestCount = clients.filter(isGuest).length;

  /* =========================================================
     RENDER
  ========================================================= */
  return (
    <div className="admin-clients">

      <header className="admin-page-head">
        <div>
          <h1>Clientes</h1>
          <p>
            {clients.length} cliente{clients.length > 1 ? 's' : ''}
            {' '}· dont {guestCount} sans compte (créées à la commande)
          </p>
        </div>
        <button
          className="admin-btn admin-btn--ghost"
          onClick={fetchClients}
          disabled={loading}
        >
          ↻ Rafraîchir
        </button>
      </header>

      <div className="admin-filters">
        <input
          type="text"
          placeholder="Rechercher par nom, email, téléphone ou ville..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="admin-input"
        />
        <div className="admin-status-filters">
          {[
            { key: 'all',     label: 'Toutes' },
            { key: 'account', label: 'Avec compte' },
            { key: 'guest',   label: 'Sans compte' },
          ].map(f => (
            <button
              key={f.key}
              className={`admin-chip ${typeFilter === f.key ? 'is-active' : ''}`}
              onClick={() => setTypeFilter(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="admin-loading"><div className="spinner" /></div>
      ) : error ? (
        <p className="admin-empty" style={{ color: '#c00' }}>{error}</p>
      ) : filtered.length === 0 ? (
        <p className="admin-empty">Aucune cliente.</p>
      ) : (
        <div className="admin-card" style={{ overflowX: 'auto' }}>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Cliente</th>
                <th>Email</th>
                <th>Téléphone</th>
                <th>Ville</th>
                <th>Commandes</th>
                <th>Total dépensé</th>
                <th>Inscrite le</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(c => {
                const isEditing = editingId === c.id;
                const isDeleting = deletingId === c.id;
                const guest = isGuest(c);

                return (
                  <tr key={c.id} style={isDeleting ? { opacity: 0.4 } : undefined}>
                    {/* Nom */}
                    <td>
                      {isEditing ? (
                        <input
                          type="text"
                          value={editName}
                          onChange={e => setEditName(e.target.value)}
                          className="admin-input"
                          style={{ minWidth: 140 }}
                        />
                      ) : (
                        <div className="admin-table__user">
                          <span className="admin-table__avatar">
                            {(c.name || 'C').charAt(0).toUpperCase()}
                          </span>
                          <div>
                            <div>{c.name || '—'}</div>
                            <small style={{ color: guest ? '#B45309' : '#16A34A' }}>
                              {guest ? 'Sans compte' : 'Compte client'}
                            </small>
                          </div>
                        </div>
                      )}
                    </td>

                    {/* Email */}
                    <td>
                      {isEditing ? (
                        guest ? (
                          <span style={{ color: '#888' }}>— (pas d'email)</span>
                        ) : (
                          <input
                            type="email"
                            value={editEmail}
                            onChange={e => setEditEmail(e.target.value)}
                            className="admin-input"
                            style={{ minWidth: 180 }}
                          />
                        )
                      ) : guest ? (
                        <span style={{ color: '#888' }}>—</span>
                      ) : (
                        c.email || '—'
                      )}
                    </td>

                    {/* Téléphone */}
                    <td>
                      {isEditing ? (
                        <input
                          type="tel"
                          value={editPhone}
                          onChange={e => setEditPhone(e.target.value)}
                          className="admin-input"
                          style={{ minWidth: 130 }}
                        />
                      ) : c.phone ? (
                        <div>
                          <div>{c.phone}</div>
                          <a
                            href={`https://wa.me/${toWhatsAppNumber(c.phone)}`}
                            target="_blank"
                            rel="noreferrer"
                            style={{ fontSize: 12, color: '#16A34A' }}
                          >
                            WhatsApp
                          </a>
                        </div>
                      ) : '—'}
                    </td>

                    {/* Ville */}
                    <td>{c.city || '—'}</td>

                    {/* Commandes */}
                    <td>{c.orders_count ?? '—'}</td>

                    {/* Total dépensé */}
                    <td>
                      {c.orders_total != null
                        ? <strong>{parseFloat(c.orders_total).toFixed(0)} DH</strong>
                        : '—'}
                    </td>

                    {/* Date */}
                    <td>
                      {c.created_at
                        ? new Date(c.created_at).toLocaleDateString('fr-FR')
                        : '—'}
                    </td>

                    {/* Actions */}
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {isEditing ? (
                        <>
                          <button
                            className="admin-btn admin-btn--primary"
                            onClick={() => saveEdit(c)}
                            disabled={saving}
                          >
                            {saving ? '…' : '✓ Enregistrer'}
                          </button>
                          <button
                            className="admin-btn admin-btn--ghost"
                            onClick={cancelEdit}
                            disabled={saving}
                            style={{ marginLeft: 6 }}
                          >
                            Annuler
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            className="admin-btn admin-btn--ghost"
                            onClick={() => setDetailClient(c)}
                          >
                            👁 Voir
                          </button>
                          <button
                            className="admin-btn admin-btn--ghost"
                            onClick={() => startEdit(c)}
                            disabled={isDeleting}
                            style={{ marginLeft: 6 }}
                          >
                            ✏️
                          </button>
                          <button
                            className="admin-btn admin-btn--danger"
                            onClick={() => deleteClient(c.id)}
                            disabled={isDeleting}
                            style={{ marginLeft: 6 }}
                          >
                            {isDeleting ? '…' : '🗑'}
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* MODAL DÉTAIL */}
      {detailClient && (
        <div
          className="admin-modal"
          onClick={() => setDetailClient(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000, padding: 20,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'white', borderRadius: 14, padding: 24,
              maxWidth: 500, width: '100%', maxHeight: '90vh', overflowY: 'auto',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h2 style={{ margin: 0, fontSize: 20 }}>
                {detailClient.name || 'Cliente'}
              </h2>
              <button
                onClick={() => setDetailClient(null)}
                style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer' }}
              >
                ✕
              </button>
            </div>

            <p>
              <strong>Type :</strong>{' '}
              {isGuest(detailClient) ? 'Sans compte (créée à la commande)' : 'Compte client'}
            </p>
            {!isGuest(detailClient) && (
              <p><strong>Email :</strong> {detailClient.email || '—'}</p>
            )}
            <p><strong>Téléphone :</strong> {detailClient.phone || '—'}</p>
            <p><strong>Ville :</strong> {detailClient.city || '—'}</p>
            <p><strong>Adresse :</strong> {detailClient.address || '—'}</p>
            <p><strong>Commandes :</strong> {detailClient.orders_count ?? '—'}</p>
            <p>
              <strong>Total dépensé :</strong>{' '}
              {detailClient.orders_total != null
                ? `${parseFloat(detailClient.orders_total).toFixed(0)} DH`
                : '—'}
            </p>
            <p>
              <strong>Inscrite le :</strong>{' '}
              {detailClient.created_at
                ? new Date(detailClient.created_at).toLocaleString('fr-FR')
                : '—'}
            </p>

            <div style={{ marginTop: 20, textAlign: 'right' }}>
              <button
                className="admin-btn admin-btn--ghost"
                onClick={() => setDetailClient(null)}
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminClients;